/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { CustomFieldService } from 'app/core/services/custom-field-service';
import { CaseService } from 'app/cm/services/case-service';
import { mergeObjects, deepClone } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { getCustomFieldDefinitionReferences } from 'app/core/models/essette-types';
import { Toaster } from 'app/shared/lib/toaster';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { caseIsReadOnly } from 'app/cm/lib/utilities';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class SaveCase extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload', defaultBindingMode: bindingMode.twoWay })
    protected case: CM.Case | null = null;

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected acuities: CM.Acuity[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected intensities: CM.Intensity[] = [];

    @bindable.array({ changeHandler: 'programGroupsChanged', defaultBindingMode: bindingMode.toView })
    protected programGroups: CM.ProgramGroup[] = [];

    protected editedCase: CM.Case | null = null;
    protected selectedProgramGroup: CM.ProgramGroup | null = null;
    protected customFieldTarget: Core.CustomFieldTarget | null = null;
    protected displayProgramGroups: CM.ProgramGroup[] = [];

    protected readonly: boolean = false;
    protected hasEditCaseDetails: boolean = false;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly caseService: CaseService,
        private readonly customFieldService: CustomFieldService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();
        this.reload();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.filterProgramGroups();
        if(!!this.case) {
            this.checkPermissions();
            this.loadCustomFieldTargets().then(() => {
                this.loadCase();
            });
        }
    }

    protected programGroupsChanged(): void {
        this.filterProgramGroups();
        if(this.case) {
            this.loadCase();
        }
    }

    ////****************** Functions ************************************************////
    protected isNotSystem: (programGroup: CM.ProgramGroup) => boolean =
        (programGroup: CM.ProgramGroup) => !programGroup.isSystem;

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasEditCaseDetails = this.accountContext.hasPermission('CM.Case.Update.EditCaseDetails');
        if(this.case) {
            this.readonly = caseIsReadOnly(this.case, this.env.user.id) || !!this.case.isClosed;
        }
    }

    private loadCase(): void {
        this.selectedProgramGroup = null;

        if(this.case) {
            this.editedCase = deepClone(this.case);

            if(this.editedCase.custom) {
                this.editedCase.custom.target = deepClone(this.customFieldTarget!) || {};
            }

            if(this.case.program?.isSystem && this.case.program.group && !!this.case.program.group?.programs?.length) {
                this.displayProgramGroups?.push(this.case.program.group);
            }

            const groupId = this.case.program && this.case.program.group && this.case.program.group.id || null;
            if(groupId && this.displayProgramGroups) {
                this.selectedProgramGroup = this.displayProgramGroups.find(pg => pg.id === groupId) || null;
            }
        }
    }

    private refreshCase(): void {
        if(this.case && this.editedCase) {
            if(this.editedCase && this.editedCase.program) {
                this.editedCase.program.group = this.selectedProgramGroup ? null : deepClone(this.selectedProgramGroup!);
            }
            mergeObjects(this.case, deepClone(this.editedCase));
        }
    }

    private filterProgramGroups() {
        // Filter for only groups with selectable valid programs
        this.displayProgramGroups = this.programGroups.filter(pg => !!pg?.programs?.length);
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public submittedChanges?: any;
    public async submit(): Promise<boolean> {
        if(await this.editCase()) {
            this.submittedChanges = this.editedCase;
            return true;
        }
        return false;
    }
    public reset(): void {
        if(this.case) {
            this.editedCase = deepClone(this.case);
            if(this.editedCase.custom) {
                this.editedCase.custom.target = deepClone(this.customFieldTarget!) || {};
            }
            const groupId = this.case.program?.group?.id;
            if(groupId && this.displayProgramGroups) {
                this.selectedProgramGroup = this.displayProgramGroups.find(pg => pg.id === groupId) || null
            }
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldTargets(): Promise<void> {
        if(!this.case || this.customFieldTarget) return;

        try {
            this.state.beginLoad();

            const includes = [
                'definitions',
                'definitions.choices'
            ];

            const fields = [
                'definitions.choices.description',
                'definitions.description',
                'definitions.property.field',
                'definitions.property.type'
            ];

            const customFieldTargets = await this.customFieldService.loadCustomFieldTargets({ targetType: 'CM.Case' }, { top: 1 }, includes, fields);
            this.customFieldTarget = customFieldTargets.results[0] || null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async editCase(): Promise<boolean> {
        if(!this.editedCase || !this.editedCase.id) { return false; }

        const saveCase: CM.Case = {
            id: this.editedCase.id,
            program: !!this.selectedProgramGroup ? getReference(this.editedCase.program) : undefined,
            acuity: getReference(this.editedCase.acuity),
            intensity: getReference(this.editedCase.intensity),
            custom: this.editedCase.custom ? getCustomFieldDefinitionReferences(this.editedCase.custom, this.customFieldTarget) : null
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.caseService.updateCase(saveCase);
            isSuccessful = response.isSuccessful;
            this.refreshCase();
            this.events.publish(new CaseChangeEvent(this.case!));
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('cm:case.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}

